import { setSelectedAccount } from "../context/accountSlice";
import accountService from "../services/account.service";

// Save account to localStorage
const saveAccountToLocalStorage = (account) => {
  try {
    localStorage.removeItem("userAccount");
    localStorage.setItem("userAccount", JSON.stringify(account));
  } catch (error) {
    console.error("Error saving account to localStorage:", error);
  }
};

// Load account from localStorage
const loadAccountFromLocalStorage = () => {
  try {
    const storedAccount = localStorage.getItem("userAccount");
    return storedAccount ? JSON.parse(storedAccount) : null;
  } catch (error) {
    return null;
  }
};

// Fetch user accounts and set the default or persisted account
export const getUserAccounts = () => {
  return async (dispatch) => {
    try {
      const response = await accountService.getUserAccounts();

      const accounts = response?.data?.body;

      if (accounts?.accounts?.length > 0) {
        const storedAccount = loadAccountFromLocalStorage();
        const accountToSet = storedAccount
          ? accounts.accounts.find((acc) => acc.urn === storedAccount.urn) ||
            accounts.accounts[0] // Use stored account or default to the first account
          : accounts.accounts[0];

        // console.log(accountToSet);

        // Dispatch the selected account to the Redux state
        saveAccountToLocalStorage(accountToSet);
        dispatch(setSelectedAccount(accountToSet));
      }
      return accounts;
    } catch (error) {
      // console.error("Error fetching user accounts:", error);
      throw error;
    }
  };
};

// Thunk action to handle account selection and persist it
export const setAccount = (account) => {
  return async (dispatch) => {
    try {
      // Dispatch the selected account to the Redux state
      dispatch(setSelectedAccount(account));
      saveAccountToLocalStorage(account);
    } catch (error) {
      console.error("Error fetching business data:", error);
    }
  };
};

export const createOutlet = async (outletData, businessUrn) => {
  return await accountService.createOutlet(outletData, businessUrn);
};

export const getOutlets = async (businessUrn) => {
  return await accountService.getOutlets(businessUrn);
};

export const deleteOutlet = async ({ businessUrn, outletUrn }) => {
  return await accountService.deleteOutlet({ businessUrn, outletUrn });
};

export const updateOutlet = async ({ businessUrn, outletUrn, payload }) => {
  return await accountService.updateOutlet({
    businessUrn,
    outletUrn,
    data: payload,
  });
};

export const deleteStampCode = async ({
  businessUrn,
  outletUrn,
  stampCode,
}) => {
  return await accountService.deleteStampCode({
    businessUrn,
    outletUrn,
    stampCode,
  });
};

export const getBusinesses = async (account_urn) => {
  const res = await accountService.getBusiness(account_urn);
  return res?.data;
};

export const createBusiness = async (account_urn, businessData) => {
  return await accountService.createBusiness(account_urn, businessData);
};

export const uploadVoucherAssets = async (accUrn, data) => {
  try {
    const res = await accountService.uploadVoucherAssets(accUrn, data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const uploadAssets = async (accUrn, data) => {
  try {
    const res = await accountService.uploadAssets(accUrn, data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const createStampCard = async (accUrn, data) => {
  try {
    const res = await accountService.createStampCard(accUrn, data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const getStampcards = async (businessUrn, account_urn) => {
  const res = await accountService.getStampcards(businessUrn, account_urn);
  return res?.data;
};

export const uploadProductImage = async (data) => {
  return await accountService.uploadProductImage(data);
};

export const createProduct = async (data) => {
  return await accountService.createProduct(data);
};

export const getAllProducts = async () => {
  const res = await accountService.getAllProducts();
  return res?.data;
};

export const getAllProductsbyBusiness = async (business_slug) => {
  const res = await accountService.getAllProductsbyBusiness(business_slug);
  return res?.data;
};

export const getProduct = async (productId) => {
  const res = await accountService.getProduct(productId);
  return res?.data;
};

export const updateProduct = async (productId, productData) => {
  return await accountService.updateProduct(productId, productData);
};

export const deleteProduct = async (productId) => {
  return await accountService.deleteProduct(productId);
};

export const getProductsCurrencies = async () => {
  const res = await accountService.getProductsCurrencies();
  return res?.data?.body;
};

export const getSchemas = async (businessUrn, type) => {
  return await accountService.getSchemas(businessUrn, type);
};

export const createMagiccode = async (accUrn, data) => {
  return await accountService.createMagiccode(accUrn, data);
};

export const getMagiccodes = async (accUrn) => {
  const res = await accountService.getMagiccodes(accUrn);
  return res?.data;
};

export const createCampaign = async (data) => {
  return await accountService.createCampaign(data);
};

export const getAllCampaigns = async () => {
  const res = await accountService.getAllCampaigns();
  return res?.data;
};

export const deleteCampaign = async (id) => {
  return await accountService.deleteCampaign(id);
};

export const getCampaign = async (id) => {
  const res = await accountService.getCampaign(id);
  return res?.data;
};

export const updateCampaign = async (id, data) => {
  return await accountService.updateCampaign(id, data);
};

export const getAccStatus = async (accUrn) => {
  const res = await accountService.getAccStatus(accUrn);
  return res?.data;
};

export const addStampCode = async (outletUrn, businessUrn, data) => {
  return await accountService.addStampCode(outletUrn, businessUrn, data);
};

export const deleteStampcard = async ({ cardUrn, accUrn }) => {
  return await accountService.deleteStampcard({ cardUrn, accUrn });
};

export const getMembers = async (accUrn) => {
  const res = await accountService.getMembers(accUrn);
  return res?.data;
};

export const sendInvite = async (data) => {
  return await accountService.sendInvite(data);
};

export const getInsightsHeadline = async (params) => {
  try {
    const res = await accountService.getInsightsHeadline(params);
    return res?.data;
  } catch (error) {
    console.error("Error fetching insights headline:", error);
    throw error;
  }
};

export const getInsightsAwardedRedeemed = async (params) => {
  const res = await accountService.getInsightsAwardedRedeemed(params);
  return res?.data;
};

export const rewardsAwardedRedeemedOverTimeByOutlet = async (params) => {
  const res =
    await accountService.rewardsAwardedRedeemedOverTimeByOutlet(params);
  return res?.data;
};

export const getInsightsVisitAvgByWeek = async (params) => {
  const res = await accountService.getInsightsVisitAvgByWeek(params);
  return res?.data;
};

export const getInsightsStampTotDist = async (params) => {
  const res = await accountService.getInsightsStampTotDist(params);
  return res?.data;
};

export const getInsightsSampsOverTimeByOutlet = async (params) => {
  const res = await accountService.getInsightsSampsOverTimeByOutlet(params);
  return res?.data;
};

export const getAllRewards = async (businessUrn) => {
  const res = await accountService.getAllRewards(businessUrn);
  return res?.data;
};

export const createReward = async (data) => {
  return await accountService.createReward(data);
};

export const getStripeAcc = async (accUrn) => {
  const res = await accountService.getStripeAcc(accUrn);
  return res?.data;
};

export const getStripeCountries = async () => {
  const res = await accountService.getStripeCountries();
  return res?.data;
};

export const createStripeAcc = async (data) => {
  const res = await accountService.createStripeAcc(data);
  return res?.data;
};

export const createStripeAccLink = async (data) => {
  const res = await accountService.createStripeAccLink(data);
  return res?.data;
};

export const exportInsights = async (params) => {
  try {
    const response = await accountService.exportInsights(params);

    // Create a blob from response
    const blob = new Blob([response.data], { type: "text/csv" });

    // Create a URL
    const url = window.URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement("a");
    link.href = url;

    // Extract filename from response header if available
    const contentDisposition = response.headers["content-disposition"];
    const fileName = contentDisposition
      ? contentDisposition.split("filename=")[1].replace(/"/g, "")
      : "insights_export.csv";

    link.setAttribute("download", fileName);

    // Append link to DOM and click it
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error("Export error:", error);
    return { success: false, error };
  }
};

export const exportInsightsAwardedRedeemedOverTimeByOutlet = async (params) => {
  try {
    const response =
      await accountService.exportInsightsAwardedRedeemedOverTimeByOutlet(
        params,
      );

    // Create a blob from response
    const blob = new Blob([response.data], { type: "text/csv" });

    // Create a URL
    const url = window.URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement("a");
    link.href = url;

    // Extract filename from response header if available
    const contentDisposition = response.headers["content-disposition"];
    const fileName = contentDisposition
      ? contentDisposition.split("filename=")[1].replace(/"/g, "")
      : "insights_export.csv";

    link.setAttribute("download", fileName);

    // Append link to DOM and click it
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error("Export error:", error);
    return { success: false, error };
  }
};

export const editBusiness = async (businessUrn, accUrn, businessData) => {
  return await accountService.editBusiness(businessUrn, accUrn, businessData);
};

export const uploadMenuAssets = async (data) => {
  return await accountService.uploadMenuAssets(data);
};

export const getPayoutsHistory = async (accUrn) => {
  const res = await accountService.getPayoutsHistory(accUrn);
  return res?.data;
};

export const getPayoutsBalance = async (accUrn) => {
  const res = await accountService.getPayoutsBalance(accUrn);
  return res?.data;
};
